import type { CompanyOption } from '@shared/components/Modals/FollowCompanyModal.types';
import type { OnboardingStepProps } from '../OnboardingWizard';
import { Add, ArrowBack, ArrowForward, Business, Close } from '@mui/icons-material';
import { Alert, Button, CircularProgress, IconButton, Slider, TextField } from '@mui/material';
import CompanyLogo from '@shared/components/CompanyLogo';
import CompanySearchAutocomplete from '@shared/components/CompanySearch';
import Select from '@shared/components/Select';
import { useApi } from '@shared/services/api';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useContext, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';

interface DealInsight {
  id?: number; // Backend ID after saving
  companyId: number;
  companyName: string;
  department: string;
  year: number;
  acv?: number;
  outcome?: 'won' | 'lost';
}

interface AddCompanyForm {
  isActive: boolean;
  companyName: string;
  url: string;
  isSubmitting: boolean;
}

const DEPARTMENTS = [
  'Customer Success',
  'Engineering',
  'Finance',
  'HR',
  'IT',
  'Legal',
  'Marketing',
  'Operations',
  'Product',
  'Sales',
  'Security',
  'Transportation',
];

const DEPARTMENT_OPTIONS = DEPARTMENTS.map((dept) => ({ name: dept, value: dept }));

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => {
  const year = currentYear - i;
  return { name: year.toString(), value: year.toString() };
});

const OUTCOME_OPTIONS = [
  { name: 'Won', value: 'won' },
  { name: 'Lost', value: 'lost' },
];

const ACV_RANGE_LABELS = ['0 - 25k', '25k - 50k', '50k - 100k', '100k - 250k', '250k - 500k', '500k - 1M', '1MM+'];

// Map ACV range index to actual dollar values (midpoint of range for backend)
const ACV_RANGE_VALUES = [
  12500, // 0-25k
  37500, // 25k-50k
  75000, // 50k-100k
  175000, // 100k-250k
  375000, // 250k-500k
  750000, // 500k-1M
  1500000, // 1MM+
];

// Helper to map ACV value back to range label
const getAcvRangeLabel = (acvValue?: number): string => {
  if (!acvValue) return '';

  if (acvValue < 25000) return ACV_RANGE_LABELS[0];
  if (acvValue < 50000) return ACV_RANGE_LABELS[1];
  if (acvValue < 100000) return ACV_RANGE_LABELS[2];
  if (acvValue < 250000) return ACV_RANGE_LABELS[3];
  if (acvValue < 500000) return ACV_RANGE_LABELS[4];
  if (acvValue < 1000000) return ACV_RANGE_LABELS[5];
  return ACV_RANGE_LABELS[6];
};

export interface DealInsightStepProps extends OnboardingStepProps {
  onChange?: (
    dealInsights: Array<{
      companyName: string;
      department: string;
      year: number;
      acv?: number;
      outcome?: 'won' | 'lost';
    }>,
  ) => void;
}

export const DealInsightStep: React.FC<DealInsightStepProps> = ({ onNext, onBack, onSkip, onChange }) => {
  const api = useApi();
  const { user } = useContext(UserContext);

  const [deals, setDeals] = useState<DealInsight[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<CompanyOption | null>(null);

  // Form state for new deal
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [department, setDepartment] = useState('');
  const [year, setYear] = useState<string>('');
  const [acv, setAcv] = useState<number>(-1); // Slider index, -1 means not set
  const [outcome, setOutcome] = useState<'' | 'won' | 'lost'>('');

  // Add company form state
  const [addCompanyForm, setAddCompanyForm] = useState<AddCompanyForm>({
    isActive: false,
    companyName: '',
    url: '',
    isSubmitting: false,
  });

  const resetForm = () => {
    setSelectedCompany(null);
    setDepartment('');
    setYear('');
    setAcv(-1);
    setOutcome('');
    setSearchValue(null);
  };

  const handleCompanySelect = useCallback((company: CompanyOption | null) => {
    if (company?.id) {
      setSelectedCompany(company);
      setSearchValue(null);
    } else {
      setSearchValue(company);
    }
  }, []);

  const handleAddCompanyClick = useCallback((searchTerm: string) => {
    setAddCompanyForm({
      isActive: true,
      companyName: searchTerm,
      url: '',
      isSubmitting: false,
    });
  }, []);

  const handleCancelAddCompany = useCallback(() => {
    setAddCompanyForm({
      isActive: false,
      companyName: '',
      url: '',
      isSubmitting: false,
    });
  }, []);

  const handleCreateCompany = useCallback(async () => {
    if (!addCompanyForm.companyName.trim()) {
      setError('Company name is required');
      return;
    }

    setAddCompanyForm((prev) => ({ ...prev, isSubmitting: true }));
    setError(null);

    try {
      const response = await api.post('/company?fromOnboarding=true', {
        companyName: addCompanyForm.companyName.trim(),
        url: addCompanyForm.url.trim() || undefined,
      });

      if (response.error) {
        setError(response.message || 'Failed to create company');
        setAddCompanyForm((prev) => ({ ...prev, isSubmitting: false }));
        return;
      }

      const newCompany: CompanyOption = {
        id: response.id,
        label: response.companyName,
        url: response.url,
      };

      setSelectedCompany(newCompany);
      setSearchValue(null);
      setAddCompanyForm({
        isActive: false,
        companyName: '',
        url: '',
        isSubmitting: false,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to create company. Please try again.');
      setAddCompanyForm((prev) => ({ ...prev, isSubmitting: false }));
    }
  }, [api, addCompanyForm]);

  const handleAddDeal = useCallback(async () => {
    if (!selectedCompany?.id || !department || !year) {
      setError('Please fill in all required fields');
      return;
    }

    const newDeal: DealInsight = {
      companyId: selectedCompany.id,
      companyName: selectedCompany.label,
      department,
      year: parseInt(year, 10),
      ...(acv >= 0 && { acv: ACV_RANGE_VALUES[acv] }),
      ...(outcome && { outcome }),
    };

    setIsSaving(true);
    setError(null);

    try {
      const isOnboarding = !!(onNext || onBack || onSkip);
      const endpoint = isOnboarding ? '/deal-insights?fromOnboarding=true' : '/deal-insights';

      const response = await api.post(endpoint, {
        companyId: newDeal.companyId,
        department: newDeal.department,
        year: newDeal.year,
        acv: newDeal.acv || null,
        outcome: newDeal.outcome || null,
      });

      if (response.error) {
        setError(response.message || 'Failed to save deal insight');
        setIsSaving(false);
        return;
      }

      // Add the backend ID to the deal
      const savedDeal: DealInsight = {
        ...newDeal,
        id: response.data?.id || response.id,
      };

      // Automatically follow the company
      if (user?.id) {
        const followResponse = await api.post(`/user/${user.id}/follow?fromOnboarding=true`, {
          userId: user.id,
          companyId: newDeal.companyId,
        });

        if (followResponse.error) {
        }
      }

      const updatedDeals = [...deals, savedDeal];
      setDeals(updatedDeals);

      // Notify parent of deal insights change
      onChange?.(
        updatedDeals.map((deal) => ({
          companyName: deal.companyName,
          department: deal.department,
          year: deal.year,
          acv: deal.acv,
          outcome: deal.outcome,
        })),
      );

      resetForm();
      setIsSaving(false);
    } catch (err) {
      setError('Failed to add deal. Please try again.');
      setIsSaving(false);
    }
  }, [selectedCompany, department, year, acv, outcome, deals, api, user, onChange]);

  const handleRemoveDeal = useCallback(
    async (index: number) => {
      const dealToRemove = deals[index];

      // If the deal has an ID, delete it from the backend
      if (dealToRemove.id) {
        try {
          await api.delete(`/deal-insights/${dealToRemove.id}`);
        } catch (err) {
          setError('Failed to remove deal. Please try again.');
          return;
        }
      }

      const updatedDeals = deals.filter((_, i) => i !== index);
      setDeals(updatedDeals);

      // Notify parent of deal insights change
      onChange?.(
        updatedDeals.map((deal) => ({
          companyName: deal.companyName,
          department: deal.department,
          year: deal.year,
          acv: deal.acv,
          outcome: deal.outcome,
        })),
      );

      setError(null);
    },
    [deals, api, onChange],
  );

  const handleContinue = () => {
    onNext();
  };

  const canAddDeal = selectedCompany?.id && department && year;
  const isOnboarding = !!onBack;

  return (
    <div className="onboarding-step" data-testid="deal-insight-step">
      <div className="onboarding-step__content">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <p className="text-secondary mb-24 leading-relaxed">
            Tell us about companies you've sold to. This helps us understand your experience and match you with relevant
            consulting opportunities.
          </p>
        </motion.div>

        {/* Add Company Form */}
        {addCompanyForm.isActive && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="onboarding-deal-form">
              <div className="onboarding-deal-form__fields">
                <div className="onboarding-deal-form__field onboarding-deal-form__field--grow">
                  <span className="onboarding-deal-form__field-label">Company Name *</span>
                  <TextField
                    value={addCompanyForm.companyName}
                    onChange={(e) => setAddCompanyForm((prev) => ({ ...prev, companyName: e.target.value }))}
                    size="small"
                    fullWidth
                    disabled={addCompanyForm.isSubmitting}
                  />
                </div>
                <div className="onboarding-deal-form__field onboarding-deal-form__field--grow">
                  <span className="onboarding-deal-form__field-label">Website URL</span>
                  <TextField
                    placeholder="Optional"
                    value={addCompanyForm.url}
                    onChange={(e) => setAddCompanyForm((prev) => ({ ...prev, url: e.target.value }))}
                    size="small"
                    fullWidth
                    disabled={addCompanyForm.isSubmitting}
                  />
                </div>
                <Button
                  variant="outlined"
                  onClick={handleCancelAddCompany}
                  disabled={addCompanyForm.isSubmitting}
                  className="onboarding-deal-form__action-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateCompany}
                  disabled={addCompanyForm.isSubmitting || !addCompanyForm.companyName.trim()}
                  startIcon={addCompanyForm.isSubmitting ? <CircularProgress size={16} color="inherit" /> : <Add />}
                  className="onboarding-btn--cta onboarding-deal-form__action-btn"
                >
                  {addCompanyForm.isSubmitting ? 'Adding...' : 'Add Company'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Add Deal Form */}
        {!addCompanyForm.isActive && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="onboarding-deal-form">
              {/* Company Search */}
              {!selectedCompany ? (
                <CompanySearchAutocomplete
                  value={searchValue}
                  onChange={handleCompanySelect}
                  label="Search for a company"
                  placeholder="Type a company name..."
                  minSearchLength={2}
                  allowNewCompany={false}
                  disabled={isSaving}
                  onAddCompanyClick={handleAddCompanyClick}
                />
              ) : (
                <>
                  {/* Selected Company Header */}
                  <div className="onboarding-deal-form__company">
                    <CompanyLogo
                      companyName={selectedCompany.label}
                      companyId={selectedCompany.id}
                      className="onboarding-company-card__logo"
                    />
                    <span className="onboarding-deal-form__company-name">{selectedCompany.label}</span>
                    <IconButton
                      onClick={() => setSelectedCompany(null)}
                      size="small"
                      className="onboarding-deal-form__company-remove"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </div>

                  {/* Deal Details Form */}
                  <div className="onboarding-deal-form__fields">
                    <div className="onboarding-deal-form__field onboarding-deal-form__field--department">
                      <span className="onboarding-deal-form__field-label">Department</span>
                      <Select
                        name="department"
                        options={DEPARTMENT_OPTIONS}
                        currentValue={department}
                        onChange={(value) => setDepartment(value as string)}
                        multiple={false}
                        size="small"
                      />
                    </div>

                    <div className="onboarding-deal-form__field onboarding-deal-form__field--year">
                      <span className="onboarding-deal-form__field-label">Year</span>
                      <Select
                        name="year"
                        options={YEAR_OPTIONS}
                        currentValue={year}
                        onChange={(value) => setYear(value as string)}
                        multiple={false}
                        size="small"
                      />
                    </div>

                    <div className="onboarding-deal-form__field onboarding-deal-form__field--outcome">
                      <span className="onboarding-deal-form__field-label">Outcome</span>
                      <Select
                        name="outcome"
                        options={OUTCOME_OPTIONS}
                        currentValue={outcome}
                        onChange={(value) => setOutcome(value as '' | 'won' | 'lost')}
                        multiple={false}
                        size="small"
                      />
                    </div>

                    <div className="onboarding-deal-form__field onboarding-deal-form__field--acv">
                      <span className="onboarding-deal-form__field-label">
                        ACV{acv >= 0 && ` · ${ACV_RANGE_LABELS[acv]}`}
                      </span>
                      <Slider
                        value={acv >= 0 ? acv : 0}
                        onChange={(_, newVal) => setAcv(newVal as number)}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(index) => ACV_RANGE_LABELS[index]}
                        marks
                        step={1}
                        min={0}
                        max={ACV_RANGE_LABELS.length - 1}
                      />
                    </div>

                    <Button
                      variant="contained"
                      onClick={handleAddDeal}
                      disabled={!canAddDeal}
                      startIcon={<Add />}
                      className="onboarding-btn--cta onboarding-deal-form__add-btn"
                      size="small"
                    >
                      Add Deal
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Added Deals List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16"
        >
          <p className="text-secondary text-sm mb-12">Your deals ({deals.length}):</p>
          <div className="onboarding-companies__list">
            {deals.length === 0 ? (
              <div className="onboarding-companies__empty">
                <Business />
                <span className="text-secondary text-sm">Search for a company above to share your deal experience</span>
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                {deals.map((deal, index) => (
                  <motion.div
                    key={`${deal.companyId}-${deal.department}-${deal.year}-${index}`}
                    initial={{ opacity: 0, scale: 0.8, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: -10 }}
                    transition={{ delay: index * 0.05 }}
                    layout
                  >
                    <div className="onboarding-company-card">
                      <span className="onboarding-company-card__number">{index + 1}</span>
                      <CompanyLogo
                        companyName={deal.companyName}
                        companyId={deal.companyId}
                        className="onboarding-company-card__logo"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="onboarding-company-card__name">{deal.companyName}</div>
                        <div className="text-secondary text-xs">
                          {deal.department} · {deal.year}
                          {deal.acv && ` · $${getAcvRangeLabel(deal.acv)}`}
                          {deal.outcome && ` · ${deal.outcome === 'won' ? '✓ Won' : '✗ Lost'}`}
                        </div>
                      </div>
                      <IconButton
                        onClick={() => handleRemoveDeal(index)}
                        size="small"
                        disabled={isSaving}
                        className="onboarding-company-card__remove"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </motion.div>

        {error && (
          <Alert severity="error" className="mt-16" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </div>

      <div className="onboarding-step__actions">
        {onBack ? (
          <Button onClick={onBack} startIcon={<ArrowBack />} color="inherit" disabled={isSaving}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <div className="onboarding-step__actions-right">
          <Button onClick={onSkip} variant="text" color="inherit" disabled={isSaving} className="onboarding-btn--skip">
            {isOnboarding ? 'Skip' : 'Close'}
          </Button>
          <Button
            data-testid="deal-insight-step-continue-button"
            onClick={handleContinue}
            variant="contained"
            endIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
            className="onboarding-btn--cta"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </Button>
        </div>
      </div>
    </div>
  );
};
